import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from '@lightspeed/flame/Core';
import { Heading1, Heading2 } from '@lightspeed/flame/Text';

import { useMoney } from '~/renderer/hooks/.';

import {
  SaleCompletedAnimation,
  SaleCompletedContainer,
  SaleCompletedWrapper,
} from './CompletedSale.style';

import type { Sale } from '~/renderer/store/receipt/types';

interface Props {
  isCompleted: boolean;
  sale: Sale | null;
  firstName?: string;
}

const CompletedSale: React.FunctionComponent<Props> = ({ isCompleted, sale, firstName }) => {
  const { t } = useTranslation();
  const { money } = useMoney();

  if (!isCompleted || !sale) return null;

  let change: null | React.ReactNode = null;
  let total: null | React.ReactNode = null;
  let discount: null | React.ReactNode = null;

  if (sale.paymentType === 'cash') {
    change = (
      <tr>
        <td data-testid="receipt-change">{t('receipt.change')}</td>
        <td style={{ textAlign: 'right' }}>
          <Heading2 data-testid="receipt-change-money">{money(sale.change)}</Heading2>
        </td>
      </tr>
    );
  }

  if (sale.calcTotal) {
    total = (
      <tr>
        <td data-testid="receipt-total">{t('receipt.total')}:</td>
        <td style={{ textAlign: 'right' }}>
          <Heading2 data-testid="receipt-total-money">{money(sale.calcTotal)}</Heading2>
        </td>
      </tr>
    );
  }

  if (sale.calcDiscount) {
    discount = (
      <tr>
        <td data-testid="receipt-discounts">{t('receipt.discounts')}</td>
        <td style={{ textAlign: 'right' }}>
          <Heading2 data-testid="receipt-discounts-money">{money(sale.calcDiscount)}</Heading2>
        </td>
      </tr>
    );
  }

  return (
    <SaleCompletedWrapper data-testid="sale-completed-wrapper">
      <SaleCompletedContainer>
        <Flex alignItems="center">
          <SaleCompletedAnimation />
          <Heading1 data-testid="receipt-sale-completed">{t('receipt.paymentSuccessful')}</Heading1>
        </Flex>
        <Heading2 pb={4} pt={2}>
          <span data-testid="receipt-thank-you">
            {firstName
              ? t('receipt.seeYouNextTimeName', { name: firstName })
              : t('receipt.seeYouNextTime')}
          </span>
        </Heading2>
        <table style={{ width: '100%' }} cellPadding="0" cellSpacing="0">
          <tbody>
            {discount}
            {total}
            {change}
          </tbody>
        </table>
      </SaleCompletedContainer>
    </SaleCompletedWrapper>
  );
};

export default CompletedSale;
